/**
 * S4 · Matrimonial — privacy-first UI.
 *
 * Routes:
 *   /matrimonial         → hub (discovery + shortcuts to profile/inbox)
 *   /matrimonial/profile → my profile create/edit
 *   /matrimonial/inbox   → incoming interests
 *
 * Privacy defaults: phone/email/address/dob HIDDEN in discovery and
 * the profile detail sheet. Contact details are shown only after a
 * mutual interest accept AND only if the profile owner has opted-in
 * to reveal them.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Heart, Shield, ChevronLeft, User as UserIcon, Bell, Ban, Flag, Send } from "lucide-react";


function Header({ title, back = true, right = null }) {
  const nav = useNavigate();
  return (
    <div className="sticky top-0 z-20 bg-white border-b border-stone-200">
      <div className="max-w-3xl mx-auto flex items-center gap-3 px-4 py-3">
        {back && <button onClick={() => nav(-1)}><ChevronLeft size={20}/></button>}
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-widest text-stone-500 font-black">Matrimonial</div>
          <div className="font-black text-stone-900 text-base flex items-center gap-2">
            <Heart size={18} className="text-rose-500"/> {title}
          </div>
        </div>
        {right}
      </div>
    </div>
  );
}


// ─── Hub / Discovery ────────────────────────────────────────────────────
export default function MatrimonialHub() {
  const [profiles, setProfiles] = useState([]);
  const [busy, setBusy] = useState(true);
  const [filters, setFilters] = useState({ gender: "", city: "", age_min: "", age_max: "" });
  const [mine, setMine] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    setBusy(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
    try {
      const { data } = await api.get(`/matrimonial/discover?${params.toString()}`);
      setProfiles(data.profiles || []);
    } catch { /* silent */ }
    setBusy(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    api.get("/matrimonial/profile/mine").then(r => setMine(r.data)).catch(() => setMine(null));
  }, []);

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="matrimonial-hub">
      <Header title="Discover" right={
        <Link to="/matrimonial/inbox" data-testid="matri-inbox-btn"
              className="rounded-full bg-stone-100 text-stone-700 text-xs font-black px-3 py-1.5 inline-flex items-center gap-1">
          <Bell size={13}/> Inbox
        </Link>
      }/>
      <div className="max-w-3xl mx-auto p-4 space-y-3">
        {/* Privacy banner */}
        <div className="rounded-2xl bg-rose-50 border border-rose-200 p-3 text-[11px] text-rose-800 flex items-start gap-2">
          <Shield size={14} className="mt-0.5 flex-none"/>
          <div>
            <b>Privacy first.</b> Contact details, precise location and documents are
            hidden by default. They are shared only after both people accept an
            interest AND the profile owner explicitly reveals them.
          </div>
        </div>

        {/* My profile CTA */}
        {!mine ? (
          <Link to="/matrimonial/profile" data-testid="matri-create-profile"
                className="block rounded-2xl bg-stone-900 text-white p-4 font-black text-sm">
            Create your matrimonial profile →
          </Link>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <Link to="/matrimonial/profile" data-testid="matri-my-profile"
                  className="block rounded-2xl bg-white border border-stone-200 p-3 text-sm text-stone-800 font-black">
              My profile <span className="block text-[10px] text-stone-500 font-normal">Visibility · Photos · Contact</span>
            </Link>
            <Link to="/matrimonial/preferences" data-testid="matri-prefs-link"
                  className="block rounded-2xl bg-white border border-stone-200 p-3 text-sm text-stone-800 font-black">
              Preferences <span className="block text-[10px] text-stone-500 font-normal">Age · Community · Education</span>
            </Link>
          </div>
        )}

        {/* Filters */}
        <div className="rounded-2xl bg-white border border-stone-200 p-3 grid grid-cols-4 gap-2">
          <select value={filters.gender} onChange={e => setFilters({...filters, gender: e.target.value})}
                  data-testid="matri-filter-gender"
                  className="col-span-2 rounded-xl border border-stone-200 px-2 py-1.5 text-xs">
            <option value="">Any gender</option>
            <option value="female">Female</option><option value="male">Male</option><option value="other">Other</option>
          </select>
          <input value={filters.city} onChange={e => setFilters({...filters, city: e.target.value})}
                 placeholder="City" data-testid="matri-filter-city"
                 className="col-span-2 rounded-xl border border-stone-200 px-2 py-1.5 text-xs"/>
          <input value={filters.age_min} onChange={e => setFilters({...filters, age_min: e.target.value})}
                 placeholder="Age min" type="number" data-testid="matri-filter-age-min"
                 className="col-span-2 rounded-xl border border-stone-200 px-2 py-1.5 text-xs"/>
          <input value={filters.age_max} onChange={e => setFilters({...filters, age_max: e.target.value})}
                 placeholder="Age max" type="number" data-testid="matri-filter-age-max"
                 className="col-span-2 rounded-xl border border-stone-200 px-2 py-1.5 text-xs"/>
          <button onClick={load} data-testid="matri-search-btn"
                  className="col-span-4 rounded-full bg-rose-600 text-white text-xs font-black py-1.5">
            Search
          </button>
        </div>

        {/* Results */}
        {busy && <div className="text-center py-10 text-stone-400">Loading…</div>}
        {!busy && !profiles.length && (
          <div className="text-center py-10 text-stone-400" data-testid="matri-empty">
            No matches yet — try widening your filters.
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          {profiles.map(p => (
            <button key={p.profile_id} onClick={() => nav(`/matrimonial/p/${p.profile_id}`)}
                    data-testid={`matri-card-${p.profile_id}`}
                    className="rounded-2xl bg-white border border-stone-200 p-3 text-left hover:border-rose-300">
              <div className="h-24 bg-stone-100 rounded-xl grid place-items-center">
                <UserIcon size={28} className="text-stone-400"/>
              </div>
              <div className="mt-2">
                <div className="font-black text-sm text-stone-900 truncate">{p.display_name}</div>
                <div className="text-[11px] text-stone-500">
                  {p.age ? `${p.age}` : "—"} · {p.gender || "—"}
                </div>
                <div className="text-[11px] text-stone-500 truncate">{p.city || "—"}</div>
                {(p.compatibility || []).length > 0 && (
                  <div className="mt-1 space-y-0.5" data-testid={`matri-compat-${p.profile_id}`}>
                    {p.compatibility.slice(0, 2).map(h => (
                      <div key={h.key}
                           className="inline-block text-[10px] font-black text-emerald-800 bg-emerald-50 rounded-full px-2 py-0.5 mr-1">
                        ✓ {h.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}


// ─── Preferences editor ───────────────────────────────────────────────
export function MatrimonialPreferences() {
  const [prefs, setPrefs] = useState({
    age_min: "", age_max: "", gender: "",
    religion: "", community: "", education: "", languages: "",
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/matrimonial/preferences").then(r => {
      const p = r.data || {};
      setPrefs({
        age_min:   p.age_min ?? "",
        age_max:   p.age_max ?? "",
        gender:    p.gender ?? "",
        religion:  (p.religion || []).join(", "),
        community: (p.community || []).join(", "),
        education: (p.education || []).join(", "),
        languages: (p.languages || []).join(", "),
      });
    }).catch(() => {});
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      const body = {
        age_min: prefs.age_min ? Number(prefs.age_min) : null,
        age_max: prefs.age_max ? Number(prefs.age_max) : null,
        gender:  prefs.gender || null,
        religion:  prefs.religion  ? prefs.religion.split(",").map(s=>s.trim()).filter(Boolean) : null,
        community: prefs.community ? prefs.community.split(",").map(s=>s.trim()).filter(Boolean) : null,
        education: prefs.education ? prefs.education.split(",").map(s=>s.trim()).filter(Boolean) : null,
        languages: prefs.languages ? prefs.languages.split(",").map(s=>s.trim()).filter(Boolean) : null,
      };
      await api.put("/matrimonial/preferences", body);
      toast.success("Preferences saved. Discovery hints will update.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cannot save");
    } finally { setBusy(false); }
  };
  const set = (k, v) => setPrefs(p => ({ ...p, [k]: v }));
  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="matri-prefs">
      <Header title="Preferences"/>
      <div className="max-w-3xl mx-auto p-4 space-y-3">
        <div className="rounded-2xl bg-white border border-stone-200 p-4 space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-black">Age range</div>
          <div className="grid grid-cols-2 gap-2">
            <input type="number" placeholder="Min age" value={prefs.age_min}
                   onChange={e => set("age_min", e.target.value)}
                   data-testid="matri-prefs-age-min"
                   className="rounded-xl border border-stone-200 px-3 py-2 text-sm"/>
            <input type="number" placeholder="Max age" value={prefs.age_max}
                   onChange={e => set("age_max", e.target.value)}
                   data-testid="matri-prefs-age-max"
                   className="rounded-xl border border-stone-200 px-3 py-2 text-sm"/>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-black pt-2">Gender</div>
          <select value={prefs.gender} onChange={e => set("gender", e.target.value)}
                  data-testid="matri-prefs-gender"
                  className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm">
            <option value="">Any</option>
            <option value="female">Female</option>
            <option value="male">Male</option>
            <option value="other">Other</option>
          </select>
          <div className="grid grid-cols-1 gap-2">
            {["religion","community","education","languages"].map(k => (
              <label key={k} className="block">
                <div className="text-[10px] uppercase tracking-widest text-stone-500 font-black">{k} <span className="normal-case text-stone-400">(comma-separated)</span></div>
                <input value={prefs[k]} onChange={e => set(k, e.target.value)}
                       data-testid={`matri-prefs-${k}`}
                       className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm"/>
              </label>
            ))}
          </div>
        </div>
        <div className="text-[11px] text-stone-500">
          Compatibility hints on discovery cards use only these preferences plus the fields other members have already made public.
          Hidden fields are never revealed.
        </div>
        <button onClick={save} disabled={busy}
                data-testid="matri-prefs-save"
                className="w-full rounded-full bg-rose-600 text-white font-black text-sm py-2 disabled:opacity-50">
          {busy ? "Saving…" : "Save preferences"}
        </button>
      </div>
    </div>
  );
}


// ─── Profile create/edit ───────────────────────────────────────────────
export function MatrimonialProfile() {
  const [mine, setMine] = useState(null);
  const [form, setForm] = useState({
    display_name: "", gender: "prefer_not_to_say", age: "", city: "",
    religion: "", community: "", marital_status: "", education: "", profession: "",
    about: "", phone: "", email: "", address: "", dob: "",
    visibility: { phone: false, email: false, address: false, dob: false,
                  photos: "connections", profile: "discoverable" },
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/matrimonial/profile/mine").then(r => { setMine(r.data); setForm(f => ({ ...f, ...r.data })); })
       .catch(() => setMine(null));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form, age: form.age ? Number(form.age) : null };
      if (mine) await api.patch("/matrimonial/profile", body);
      else      await api.post("/matrimonial/profile", body);
      toast.success("Profile saved");
      const r = await api.get("/matrimonial/profile/mine");
      setMine(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cannot save");
    } finally { setBusy(false); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setVis = (k, v) => setForm(f => ({ ...f, visibility: { ...f.visibility, [k]: v } }));

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="matri-profile-form">
      <Header title="My profile"/>
      <div className="max-w-3xl mx-auto p-4 space-y-3">
        <div className="rounded-2xl bg-white border border-stone-200 p-3 space-y-2">
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-black">Display name</div>
            <input value={form.display_name} onChange={e => set("display_name", e.target.value)}
                   data-testid="matri-input-name"
                   className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm"/>
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-black">Gender</div>
              <select value={form.gender} onChange={e => set("gender", e.target.value)}
                      className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm">
                <option value="prefer_not_to_say">Prefer not to say</option>
                <option value="female">Female</option><option value="male">Male</option><option value="other">Other</option>
              </select>
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-black">Age</div>
              <input type="number" value={form.age} onChange={e => set("age", e.target.value)}
                     className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm"/>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="City" value={form.city} onChange={e => set("city", e.target.value)}
                   className="rounded-xl border border-stone-200 px-3 py-2 text-sm"/>
            <input placeholder="Religion" value={form.religion} onChange={e => set("religion", e.target.value)}
                   className="rounded-xl border border-stone-200 px-3 py-2 text-sm"/>
            <input placeholder="Community" value={form.community} onChange={e => set("community", e.target.value)}
                   className="rounded-xl border border-stone-200 px-3 py-2 text-sm"/>
            <input placeholder="Education" value={form.education} onChange={e => set("education", e.target.value)}
                   className="rounded-xl border border-stone-200 px-3 py-2 text-sm"/>
            <input placeholder="Profession" value={form.profession} onChange={e => set("profession", e.target.value)}
                   className="rounded-xl border border-stone-200 px-3 py-2 text-sm col-span-2"/>
          </div>
          <textarea placeholder="About you (public)" value={form.about} onChange={e => set("about", e.target.value)}
                    className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm" rows={3}/>
        </div>

        {/* Contact — private */}
        <div className="rounded-2xl bg-stone-900 text-white p-3 space-y-2" data-testid="matri-private-section">
          <div className="text-[10px] uppercase tracking-widest text-stone-400 font-black flex items-center gap-1">
            <Shield size={12}/> Private details (hidden by default)
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="Phone" value={form.phone || ""} onChange={e => set("phone", e.target.value)}
                   data-testid="matri-private-phone"
                   className="rounded-xl bg-stone-800 border border-stone-700 px-3 py-2 text-sm text-white"/>
            <input placeholder="Email" value={form.email || ""} onChange={e => set("email", e.target.value)}
                   data-testid="matri-private-email"
                   className="rounded-xl bg-stone-800 border border-stone-700 px-3 py-2 text-sm text-white"/>
            <input placeholder="Address" value={form.address || ""} onChange={e => set("address", e.target.value)}
                   className="rounded-xl bg-stone-800 border border-stone-700 px-3 py-2 text-sm text-white col-span-2"/>
            <input placeholder="DOB (YYYY-MM-DD)" value={form.dob || ""} onChange={e => set("dob", e.target.value)}
                   className="rounded-xl bg-stone-800 border border-stone-700 px-3 py-2 text-sm text-white"/>
          </div>
          <div className="text-[11px] text-stone-400">Check a box to reveal that field to accepted connections only.</div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {["phone","email","address","dob"].map(k => (
              <label key={k} className="inline-flex items-center gap-2">
                <input type="checkbox" checked={!!form.visibility?.[k]}
                       onChange={e => setVis(k, e.target.checked)}
                       data-testid={`matri-visibility-${k}`}/>
                Reveal {k}
              </label>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs pt-2 border-t border-stone-700">
            <label>Photos: <select value={form.visibility?.photos || "connections"}
                     onChange={e => setVis("photos", e.target.value)}
                     className="rounded-md bg-stone-800 px-2 py-1 text-white">
                <option value="private">Only me</option>
                <option value="connections">Accepted connections</option>
                <option value="public">Public (discovery)</option>
              </select></label>
            <label>Profile: <select value={form.visibility?.profile || "discoverable"}
                     onChange={e => setVis("profile", e.target.value)}
                     className="rounded-md bg-stone-800 px-2 py-1 text-white">
                <option value="discoverable">Discoverable</option>
                <option value="hidden">Hidden</option>
              </select></label>
          </div>
        </div>

        <button onClick={save} disabled={busy}
                data-testid="matri-save-btn"
                className="w-full rounded-full bg-rose-600 text-white font-black text-sm py-2 disabled:opacity-50">
          {busy ? "Saving…" : mine ? "Save changes" : "Create profile"}
        </button>
      </div>
    </div>
  );
}


// ─── Public profile view (interest + report + block) ────────────────────
export function MatrimonialProfileView() {
  const { profile_id } = useLocationParam();
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  useEffect(() => {
    api.get(`/matrimonial/profile/${profile_id}`)
      .then(r => setP(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Cannot load"));
  }, [profile_id]);

  const express = async () => {
    setBusy(true);
    try {
      await api.post("/matrimonial/interest", { to_profile_id: profile_id });
      toast.success("Interest sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cannot send");
    } finally { setBusy(false); }
  };
  const block = async () => {
    if (!window.confirm("Block this profile? You won't see them again.")) return;
    await api.post("/matrimonial/block", { profile_id });
    toast.success("Blocked");
  };

  if (!p) return <div className="p-10 text-center text-stone-400">Loading…</div>;
  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="matri-profile-view">
      <Header title={p.display_name || "Profile"}/>
      <div className="max-w-3xl mx-auto p-4 space-y-3">
        <div className="rounded-2xl bg-white border border-stone-200 p-4">
          <div className="text-lg font-black text-stone-900">{p.display_name}</div>
          <div className="text-xs text-stone-500">{p.age ? `${p.age} · ` : ""}{p.gender || "—"}{p.city ? ` · ${p.city}` : ""}</div>
          <div className="mt-2 text-sm text-stone-700">{p.about}</div>
          {p.photos_hidden && (
            <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 rounded-full px-2 py-0.5 inline-block">
              Photos hidden — reveal after mutual accept
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2 text-sm">
          {p.religion && <Info label="Religion" value={p.religion}/>}
          {p.community && <Info label="Community" value={p.community}/>}
          {p.education && <Info label="Education" value={p.education}/>}
          {p.profession && <Info label="Profession" value={p.profession}/>}
          {p.marital_status && <Info label="Marital status" value={p.marital_status}/>}
        </div>

        {(p.compatibility || []).length > 0 && (
          <div className="rounded-2xl bg-white border border-stone-200 p-3 space-y-1"
               data-testid="matri-compat-list">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-black">Why we're showing this profile</div>
            {p.compatibility.map(h => (
              <div key={h.key} className="text-xs text-stone-800">
                <span className="text-emerald-700 font-black">✓</span> {h.label}
              </div>
            ))}
            <div className="text-[10px] text-stone-400 mt-1">Based on your preferences and information both of you have chosen to share.</div>
          </div>
        )}

        {(p.phone || p.email || p.address) && (
          <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-3 text-sm">
            <div className="text-[10px] uppercase font-black text-emerald-700 tracking-widest">Shared with you</div>
            {p.phone && <div>Phone: <b>{p.phone}</b></div>}
            {p.email && <div>Email: <b>{p.email}</b></div>}
            {p.address && <div>Address: <b>{p.address}</b></div>}
          </div>
        )}

        <div className="flex gap-2">
          <button onClick={express} disabled={busy} data-testid="matri-interest-btn"
                  className="flex-1 rounded-full bg-rose-600 text-white font-black text-xs py-2">
            <Send size={13} className="inline"/> Express interest
          </button>
          <button onClick={() => setReportOpen(true)} data-testid="matri-report-btn"
                  className="rounded-full bg-amber-50 text-amber-700 font-black text-xs px-3 py-2">
            <Flag size={13} className="inline"/> Report
          </button>
          <button onClick={block} data-testid="matri-block-btn"
                  className="rounded-full bg-stone-100 text-stone-700 font-black text-xs px-3 py-2">
            <Ban size={13} className="inline"/> Block
          </button>
        </div>
      </div>
      {reportOpen && <ReportSheet profile_id={profile_id} onClose={() => setReportOpen(false)}/>}
    </div>
  );
}


function Info({ label, value }) {
  return (
    <div className="rounded-xl bg-white border border-stone-200 p-2">
      <div className="text-[10px] uppercase text-stone-500 font-black tracking-widest">{label}</div>
      <div className="text-sm text-stone-800 font-black">{value}</div>
    </div>
  );
}


function ReportSheet({ profile_id, onClose }) {
  const [reason, setReason] = useState("");
  const submit = async () => {
    if (!reason.trim()) return toast.error("Please describe the issue");
    try {
      await api.post("/matrimonial/report", { profile_id, reason });
      toast.success("Report submitted — our team will review");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cannot report");
    }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-2" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()}
           data-testid="matri-report-sheet">
        <div className="font-black text-stone-900">Report profile</div>
        <textarea value={reason} onChange={e => setReason(e.target.value)}
                  placeholder="Describe the issue (spam, abuse, fake profile, …)" rows={3}
                  data-testid="matri-report-reason"
                  className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm"/>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 rounded-full bg-stone-100 text-stone-700 font-black text-xs py-2">Cancel</button>
          <button onClick={submit} data-testid="matri-report-submit"
                  className="flex-1 rounded-full bg-amber-600 text-white font-black text-xs py-2">Submit</button>
        </div>
      </div>
    </div>
  );
}


// ─── Inbox (incoming interests) ────────────────────────────────────────
export function MatrimonialInbox() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const load = () => api.get("/matrimonial/interests/incoming")
      .then(r => setItems(r.data.interests || [])).finally(() => setBusy(false));
  useEffect(() => { load(); }, []);
  const decide = async (id, decision) => {
    try {
      await api.post(`/matrimonial/interest/${id}/decide`, { decision });
      toast.success(decision === "accept" ? "Accepted — you're connected" : "Interest declined");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cannot decide");
    }
  };
  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="matri-inbox">
      <Header title="Interests inbox"/>
      <div className="max-w-3xl mx-auto p-4 space-y-2">
        {busy && <div className="text-center py-10 text-stone-400">Loading…</div>}
        {!busy && !items.length && (
          <div className="text-center py-10 text-stone-400">Nothing waiting for you.</div>
        )}
        {items.map(i => (
          <div key={i.interest_id} className="rounded-2xl bg-white border border-stone-200 p-3"
               data-testid={`matri-interest-${i.interest_id}`}>
            <div className="text-sm text-stone-800 font-black">Interest from a member</div>
            <div className="text-[11px] text-stone-500 mt-0.5">{new Date(i.created_at).toLocaleString()}</div>
            {i.message && <div className="mt-2 text-sm text-stone-700 italic">"{i.message}"</div>}
            <div className="flex gap-2 mt-3">
              <button onClick={() => decide(i.interest_id, "accept")}
                      data-testid={`matri-accept-${i.interest_id}`}
                      className="flex-1 rounded-full bg-rose-600 text-white font-black text-xs py-2">Accept</button>
              <button onClick={() => decide(i.interest_id, "reject")}
                      data-testid={`matri-reject-${i.interest_id}`}
                      className="flex-1 rounded-full bg-stone-100 text-stone-700 font-black text-xs py-2">Decline</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// Tiny helper to read the :profile_id param without importing useParams
// (kept local so the file stays self-contained).
function useLocationParam() {
  const [id, setId] = useState("");
  useEffect(() => {
    const p = window.location.pathname.split("/").pop();
    setId(p);
  }, []);
  return { profile_id: id };
}
